import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, from, fromEvent, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { IVehicleAddress } from '../interfaces/task-info';
import { IGetAddressIOSettings } from '../interfaces/get-address-io-auto-completion-settings';
import { MapService } from './map.service';
import { CustomerSitesService } from './customer-sites.service';
import { ICustomerSites } from '../interfaces/customer-sites';
import * as stringSimilarity from 'string-similarity';
import { AddressDTO } from '../interfaces/address';

export enum SuggestionSource {
  Main = 'main',
  Loqate = 'loqate',
}

interface AutoCompleteAddressSuggestion {
  address: string;
  url: string;
  id: string;
}

interface AutoCompleteAddressResponse {
  suggestions: AutoCompleteAddressSuggestion[];
}

interface AutoCompleteAddressDetails {
  postcode: string;
  latitude: number;
  longitude: number;
  formatted_address: string[];
  thoroughfare: string;
  building_name: string;
  sub_building_name: string;
  sub_building_number: string;
  building_number: string;
  line_1: string;
  line_2: string;
  line_3: string;
  line_4: string;
  locality: string;
  town_or_city: string;
  county: string;
  district: string;
  country: string;
  residential: boolean;
}

export interface AutoCompleteAddressDetailsFormatted {
  postcode: string;
  latitude: number;
  longitude: number;
  thoroughfare: string;
  building_name: string;
  sub_building_name: string;
  sub_building_number: string;
  building_number: string;
  line_1: string;
  line_2: string;
  line_3: string;
  line_4: string;
  locality: string;
  town_or_city: string;
  county: string;
  district: string;
  country: string;
  residential: boolean;
  businessName: string;
}

interface AutoCompleteAddressValidationResultHeader {
  status: number;
  address: AutoCompleteAddressValidationResult;
}

interface AutoCompleteAddressValidationResult {
  postcode: string;
  latitude: number;
  longitude: number;
  formatted_address: string[];
  thoroughfare: string;
  building_name: string;
  sub_building_name: string;
  sub_building_number: string;
  building_number: string;
  line_1: string;
  line_2: string;
  line_3: string;
  line_4: string;
  locality: string;
  town_or_city: string;
  county: string;
  district: string;
  country: string;
  residential: boolean;
}

interface AutoCompleteAddressSuggestionLoqate {
  Id: string;
  Type: string;
  Text: string;
  Highlight: string;
  Description: string;
}

interface AutoCompleteAddressResponseLoqate {
  Items: AutoCompleteAddressSuggestionLoqate[];
}

export interface AutoCompleteAddressDetailsLoqate {
  Id: string;
  DomesticId: string;
  Language: string;
  LanguageAlternatives: string;
  Department: string;
  Company: string;
  SubBuilding: string;
  BuildingNumber: string;
  BuildingName: string;
  SecondaryStreet: string;
  Street: string;
  Block: string;
  Neighbourhood: string;
  District: string;
  City: string;
  Line1: string;
  Line2: string;
  Line3: string;
  Line4: string;
  Line5: string;
  AdminAreaName: string;
  AdminAreaCode: string;
  Province: string;
  ProvinceName: string;
  ProvinceCode: string;
  PostalCode: string;
  CountryName: string;
  CountryIso2: string;
  CountryIso3: string;
  CountryIsoNumber: string;
  SortingNumber1: string;
  SortingNumber2: string;
  Barcode: string;
  POBoxNumber: string;
  Label: string;
  Type: string;
  DataLevel: string;
  Field1: string;
  Field2: string;
}

interface AutoCompleteAddressDetailsResponseLoqate {
  Items: AutoCompleteAddressDetailsLoqate[];
}

export interface UnifiedAddressSuggestion {
  address: string;
  id: string;
  source: SuggestionSource;
}

@Injectable({
  providedIn: 'root',
})
export class AddressAutoCompletionService {
  private apiKey = '';
  private loqateApiKey = '';
  private apiUrl = 'https://api.getAddress.io/autocomplete';
  private apiUrlForDetails = 'https://api.getAddress.io/get';
  private apiUrlForValidate = 'https://api.getAddress.io/validate';
  private loqateApiUrl = 'https://api.addressy.com/Capture/Interactive/Find/v1.1/json3.ws';
  private loqateApiUrlForDetails =
    'https://api.addressy.com/Capture/Interactive/Retrieve/v1.2/json3.ws';
  private renderer: Renderer2;

  constructor(
    private http: HttpClient,
    rendererFactory: RendererFactory2,
    private mapService: MapService,
    private customerSitesService: CustomerSitesService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);

    this.getAddressIOAutoCompletionSettings().then((settings) => {
      this.apiKey = settings.key;
    });

    this.mapService.GetPCAKey().then((result) => {
      if (result) {
        this.loqateApiKey = result.key;
      }
    });
  }

  /**
   * Fetches API settings for getAddress.io.
   */
  public getAddressIOAutoCompletionSettings(): Promise<IGetAddressIOSettings> {
    return this.http
      .get<IGetAddressIOSettings>('api/map/getAddressIOAutoCompletionSettings')
      .toPromise();
  }

  /**
   * Attaches the address auto-completion functionality to a set of input elements,
   * converting the selected address to the IVehicleAddress format.
   * @param elements - Configuration for the input elements.
   * @param callback - Function to call with the selected and converted address.
   * @param savedSiteCallback - Function to call when a saved site is selected.
   * @param manualEditCallback - Function to call when the user opts for manual editing.
   */
  public useWithVehicleAddressConverter(
    elements: {
      element: string;
      field: string;
      parentContainer: string;
      showSavedSites: boolean;
      customerId?: number;
    }[],
    callback: (address: IVehicleAddress) => void = () => {},
    savedSiteCallback: (site: ICustomerSites) => void = () => {},
    manualEditCallback: () => void = () => {},
    zIndex: number = null,
  ): void {
    this.attachAutocomplete(
      elements,
      (address) => {
        const vehicleAddress: IVehicleAddress = this.convertToVehicleAddress(address);
        callback(vehicleAddress);
      },
      (loqateAddress) => {
        const vehicleAddress: IVehicleAddress = this.convertLoqateToVehicleAddress(loqateAddress);
        callback(vehicleAddress);
      },
      savedSiteCallback,
      manualEditCallback,
      (mapServiceAddress) => {
        const vehicleAddress: IVehicleAddress =
          this.convertMapServiceToVehicleAddress(mapServiceAddress);
        callback(vehicleAddress);
      },
      zIndex,
    );
  }

  /**
   * Attaches the address auto-completion functionality to a set of input elements,
   * providing the selected address in the default formatted response.
   * @param elements - Configuration for the input elements.
   * @param callback - Function to call with the selected and formatted address.
   * @param savedSiteCallback - Function to call when a saved site is selected.
   * @param manualEditCallback - Function to call when the user opts for manual editing.
   */
  useWithDefaultAddressResponse(
    elements: {
      element: string;
      field: string;
      parentContainer: string;
      showSavedSites: boolean;
    }[],
    callback: (address: AutoCompleteAddressDetailsFormatted) => void,
    savedSiteCallback: (site: ICustomerSites) => void = () => {},
    manualEditCallback: () => void = () => {},
    zIndex: number = null,
  ): void {
    this.attachAutocomplete(
      elements,
      (address) => {
        const formattedAddress: AutoCompleteAddressDetailsFormatted =
          this.formatStandardAddress(address);
        callback(formattedAddress);
      },
      (loqateAddress) => {
        const formattedAddress: AutoCompleteAddressDetailsFormatted =
          this.formatLoqateToStandardAddress(loqateAddress);
        callback(formattedAddress);
      },
      savedSiteCallback,
      manualEditCallback,
      () => {},
      zIndex,
    );
  }

  /**
   * Fetches address suggestions from getAddress.io based on a search term.
   * @param searchTerm - The term to search for.
   * @returns An observable of address suggestions.
   */
  public getSuggestions(searchTerm: string): Observable<AutoCompleteAddressSuggestion[]> {
    if (!searchTerm.trim()) {
      return new Observable<AutoCompleteAddressSuggestion[]>((observer) => observer.next([]));
    }
    const url = `${this.apiUrl}/${encodeURIComponent(searchTerm.trim())}?api-key=${this.apiKey}`;
    return this.http
      .get<AutoCompleteAddressResponse>(url)
      .pipe(map((response) => response.suggestions))
      .catch((err) => {
        console.log(err);
        return Observable.of([]);
      });
  }

  /**
   * Fetches detailed information for a specific address from getAddress.io using its ID.
   * @param id - The ID of the address suggestion.
   * @returns An observable of detailed address information.
   */
  public getAddressDetails(id: string): Observable<AutoCompleteAddressDetails> {
    const url = `${this.apiUrlForDetails}/${id}?api-key=${this.apiKey}`;
    return this.http.get<AutoCompleteAddressDetails>(url);
  }

  /**
   * Attaches the autocomplete functionality to input elements.
   * It listens for input events, fetches suggestions from multiple sources (getAddress.io, Loqate, saved customer sites),
   * and displays them to the user.
   */
  private attachAutocomplete(
    elements: {
      element: string;
      field: string;
      parentContainer: string;
      showSavedSites: boolean;
      customerId?: number;
    }[],
    callback: (address: AutoCompleteAddressDetails) => void,
    loqateCallback: (address: AutoCompleteAddressDetailsLoqate) => void,
    savedSiteCallback: (site: ICustomerSites) => void,
    manualEditCallback: () => void,
    mapServiceCallback?: (address: AddressDTO) => void,
    zIndex: number = null,
  ): void {
    setTimeout(() => {
      elements.forEach(({ element, parentContainer, showSavedSites, customerId }) => {
        const inputElement = document.getElementById(element) as HTMLInputElement;
        if (inputElement) {
          fromEvent(inputElement, 'input')
            .pipe(
              map((event) => (event.target as HTMLInputElement).value),
              debounceTime(300),
              distinctUntilChanged(),
              switchMap((term) => {
                //const suggestions$ = this.getSuggestions(term);
                const customerSites$ = showSavedSites
                  ? this.getCustomerSites(term, customerId)
                  : of([]);
                const loqateSuggestions$ = this.getSuggestionsFromLoqate(term);
                return combineLatest([customerSites$, loqateSuggestions$]);
              }),
            )
            .subscribe(([customerSites, loqateSuggestions]) => {
              let suggestions: AutoCompleteAddressSuggestion[] = [];

              const filteredLoqateSuggestions = this.filterLoqateSuggestions(
                suggestions,
                loqateSuggestions,
              );

              const unifiedSuggestions = this.transformSuggestions(
                suggestions,
                filteredLoqateSuggestions,
              );

              this.displaySuggestions(
                inputElement,
                unifiedSuggestions,
                customerSites,
                parentContainer,
                callback,
                loqateCallback,
                savedSiteCallback,
                manualEditCallback,
                mapServiceCallback,
                zIndex,
              );
            });
        }
      });
    }, 200);
  }

  /**
   * Manages the rendering of the suggestion box, including saved sites, other results, and a manual edit option.
   */
  private displaySuggestions(
    inputElement: HTMLInputElement,
    suggestions: UnifiedAddressSuggestion[],
    customerSites: ICustomerSites[],
    parentContainer: string,
    callback: (address: AutoCompleteAddressDetails) => void,
    loqateCallback: (address: AutoCompleteAddressDetailsLoqate) => void,
    savedSiteCallback: (site: ICustomerSites) => void,
    manualEditCallback: () => void,
    mapServiceCallback?: (address: AddressDTO) => void,
    zIndex: number = null,
  ): void {
    const suggestionBox = this.getOrCreateSuggestionBox(zIndex);
    const parentContainerElement =
      parentContainer.length > 0 ? document.getElementById(parentContainer) : null;
    const searchTerm = inputElement.value.trim().toLowerCase();

    this.clearSuggestionBoxIfNecessary(suggestionBox, suggestions.length, customerSites.length);
    this.renderSavedSites(
      inputElement,
      suggestionBox,
      customerSites,
      searchTerm,
      savedSiteCallback,
    );
    this.renderOtherResultsDivider(suggestionBox, suggestions.length, customerSites.length);
    this.renderUnifiedSuggestions(
      inputElement,
      suggestionBox,
      suggestions,
      searchTerm,
      callback,
      loqateCallback,
      mapServiceCallback,
    );
    this.renderNoResultsManualEdit(
      inputElement,
      suggestionBox,
      manualEditCallback,
      suggestions.length > 0 || customerSites.length > 0,
    );
    this.positionSuggestionBox(suggestionBox, inputElement, parentContainerElement);
    this.attachClickOutsideListener(suggestionBox, inputElement);
  }

  /**
   * Retrieves or creates the suggestion box DOM element.
   */
  private getOrCreateSuggestionBox(zIndex = null): HTMLElement {
    let suggestionBox = document.getElementById('address-auto-complete-suggestion-box');

    if (!suggestionBox) {
      suggestionBox = this.renderer.createElement('ul');
      if (zIndex != null) {
        this.renderer.setStyle(suggestionBox, 'z-index', zIndex.toString());
      }
      this.renderer.setAttribute(suggestionBox, 'id', 'address-auto-complete-suggestion-box');
      this.renderer.addClass(suggestionBox, 'address-auto-complete-suggestion-box');
      this.renderer.appendChild(document.body, suggestionBox);
    }

    return suggestionBox;
  }

  /**
   * Clears or removes the suggestion box based on whether there are suggestions to display.
   */
  private clearSuggestionBoxIfNecessary(
    suggestionBox: HTMLElement,
    suggestionsCount: number,
    customerSitesCount: number,
  ): void {
    if (suggestionsCount > 0 || customerSitesCount > 0) {
      suggestionBox.textContent = '';
    } else {
      if (suggestionBox.parentNode) {
        this.renderer.removeChild(document.body, suggestionBox);
      }
    }
  }

  /**
   * Renders the list of saved customer sites in the suggestion box.
   */
  private renderSavedSites(
    inputElement: HTMLInputElement,
    suggestionBox: HTMLElement,
    customerSites: ICustomerSites[],
    searchTerm: string,
    savedSiteCallback: (site: ICustomerSites) => void,
  ): void {
    if (customerSites.length === 0) return;

    const divider = this.renderer.createElement('li');
    this.renderer.addClass(divider, 'suggestion-divider-heading');
    this.renderer.appendChild(divider, this.renderer.createText('Saved Sites'));
    this.renderer.appendChild(suggestionBox, divider);

    customerSites.forEach((site) => {
      const siteItem = this.renderer.createElement('li');
      this.renderer.addClass(siteItem, 'address-auto-complete-suggestion-item');

      const fullSavedSiteAddress = this.customerSitesService.formatSiteAddress(site);
      this.appendHighlightedText(siteItem, fullSavedSiteAddress, searchTerm);

      this.renderer.listen(siteItem, 'click', () => {
        inputElement.value = '';
        savedSiteCallback(site);
        if (suggestionBox.parentNode) {
          this.renderer.removeChild(document.body, suggestionBox);
        }
      });

      this.renderer.appendChild(suggestionBox, siteItem);
    });
  }

  /**
   * Renders a divider titled 'Other Results' if both saved sites and other suggestions are present.
   */
  private renderOtherResultsDivider(
    suggestionBox: HTMLElement,
    suggestionsCount: number,
    customerSitesCount: number,
  ): void {
    if (suggestionsCount > 0 && customerSitesCount > 0) {
      const divider = this.renderer.createElement('li');
      this.renderer.addClass(divider, 'suggestion-divider-heading');
      this.renderer.addClass(divider, 'suggestion-divider-heading-secondary');
      this.renderer.appendChild(divider, this.renderer.createText('Other Results'));
      this.renderer.appendChild(suggestionBox, divider);
    }
  }

  /**
   * Renders the unified list of address suggestions from all sources.
   */
  private renderUnifiedSuggestions(
    inputElement: HTMLInputElement,
    suggestionBox: HTMLElement,
    suggestions: UnifiedAddressSuggestion[],
    searchTerm: string,
    callback: (address: AutoCompleteAddressDetails) => void,
    loqateCallback: (address: AutoCompleteAddressDetailsLoqate) => void,
    mapServiceCallback?: (address: AddressDTO) => void,
  ): void {
    if (suggestions.length === 0) return;

    suggestions.sort((a, b) => {
      const regex = /(?:Apartment|Flat)?\s*(\d+)/i;
      const prefixA = parseInt(a.address.match(regex)?.[1] || '0', 10);
      const prefixB = parseInt(b.address.match(regex)?.[1] || '0', 10);

      if (prefixA !== prefixB) {
        return prefixA - prefixB;
      }

      return a.address.localeCompare(b.address);
    });

    suggestions.forEach((suggestion) => {
      const suggestedAddress = this.renderer.createElement('li');
      this.renderer.addClass(suggestedAddress, 'address-auto-complete-suggestion-item');

      this.renderer.listen(suggestedAddress, 'click', () => {
        if (suggestion.source === SuggestionSource.Main) {
          this.handleMainSuggestionClick(
            suggestion,
            inputElement,
            suggestionBox,
            callback,
            mapServiceCallback,
          );
        } else if (suggestion.source === SuggestionSource.Loqate) {
          this.handleLoqateSuggestionClick(
            suggestion,
            inputElement,
            suggestionBox,
            loqateCallback,
            mapServiceCallback,
          );
        }
      });

      this.appendHighlightedText(suggestedAddress, suggestion.address, searchTerm);
      this.renderer.appendChild(suggestionBox, suggestedAddress);
    });
  }

  /**
   * Handles the click event for a suggestion from the main provider (getAddress.io).
   * It fetches full address details and uses a fallback if necessary.
   */
  private handleMainSuggestionClick(
    suggestion: UnifiedAddressSuggestion,
    inputElement: HTMLInputElement,
    suggestionBox: HTMLElement,
    callback: (address: AutoCompleteAddressDetails) => void,
    mapServiceCallback?: (address: AddressDTO) => void,
  ): void {
    this.getAddressDetails(suggestion.id).subscribe({
      next: (fullAddress) => {
        if (!this.isLatLngValid(fullAddress.latitude, fullAddress.longitude)) {
          if (mapServiceCallback) {
            const formattedAddress = this.createFormattedAddressStringFromGetAddress(fullAddress);
            this.useMapServiceFallback(
              formattedAddress,
              inputElement,
              suggestionBox,
              mapServiceCallback,
            );
            return;
          }
        }

        inputElement.value = '';
        callback(fullAddress);

        if (suggestionBox.parentNode) {
          this.renderer.removeChild(document.body, suggestionBox);
        }
      },
      error: (error) => {
        if (mapServiceCallback) {
          console.log('Main address lookup failed, using MapService fallback', error);
          this.useMapServiceFallback(
            suggestion.address,
            inputElement,
            suggestionBox,
            mapServiceCallback,
          );
        }
      },
    });
  }

  /**
   * Handles the click event for a suggestion from Loqate.
   * It fetches full address details and includes logic for coordinate validation and fallbacks.
   */
  private handleLoqateSuggestionClick(
    suggestion: UnifiedAddressSuggestion,
    inputElement: HTMLInputElement,
    suggestionBox: HTMLElement,
    loqateCallback: (address: AutoCompleteAddressDetailsLoqate) => void,
    mapServiceCallback?: (address: AddressDTO) => void,
  ): void {
    this.getLoqateAddressDetails(suggestion.id).subscribe({
      next: (fullAddressResponse) => {
        if (
          !fullAddressResponse ||
          !fullAddressResponse.Items ||
          fullAddressResponse.Items.length === 0
        ) {
          if (mapServiceCallback) {
            console.log('Loqate returned empty result, using MapService fallback');
            this.useMapServiceFallback(
              suggestion.address,
              inputElement,
              suggestionBox,
              mapServiceCallback,
            );
          }
          return;
        }

        const loqateAddress = fullAddressResponse.Items[0];

        const hasRequiredLoqateParts =
          loqateAddress.PostalCode &&
          loqateAddress.City &&
          (loqateAddress.Line1 || (loqateAddress.Street && loqateAddress.BuildingNumber));

        if (
          !this.isLatLngValid(loqateAddress.Field1, loqateAddress.Field2) &&
          hasRequiredLoqateParts
        ) {
          this.mapService
            .GetAddress(suggestion.address)
            .then((mapResult) => {
              if (mapResult && this.isLatLngValid(mapResult.lat, mapResult.lng)) {
                loqateAddress.Field1 = mapResult.lat.toString();
                loqateAddress.Field2 = mapResult.lng.toString();
                inputElement.value = '';
                loqateCallback(loqateAddress);
                if (suggestionBox.parentNode) {
                  this.renderer.removeChild(document.body, suggestionBox);
                }
              } else {
                console.log(
                  'MapService fallback for Loqate coordinates also failed or returned invalid coordinates.',
                );
                this.useMapServiceFallback(
                  suggestion.address,
                  inputElement,
                  suggestionBox,
                  mapServiceCallback,
                );
              }
            })
            .catch((mapError) => {
              console.error('MapService coordinate fallback failed', mapError);
              this.useMapServiceFallback(
                suggestion.address,
                inputElement,
                suggestionBox,
                mapServiceCallback,
              );
            });
        } else if (!hasRequiredLoqateParts) {
          if (mapServiceCallback) {
            console.log(
              'Loqate missing required address parts, using MapService fallback entirely.',
            );
            const formattedAddress = this.createFormattedAddressStringFromLoqate(loqateAddress);
            this.useMapServiceFallback(
              formattedAddress || suggestion.address,
              inputElement,
              suggestionBox,
              mapServiceCallback,
            );
          }
        } else {
          inputElement.value = '';
          loqateCallback(loqateAddress);
          if (suggestionBox.parentNode) {
            this.renderer.removeChild(document.body, suggestionBox);
          }
        }
      },
      error: (error) => {
        if (mapServiceCallback) {
          console.log('Loqate address lookup failed, using MapService fallback', error);
          this.useMapServiceFallback(
            suggestion.address,
            inputElement,
            suggestionBox,
            mapServiceCallback,
          );
        }
      },
    });
  }

  /**
   * Renders an "Address Not Found" message with a "Manual Edit" link in the suggestion box.
   */
  private renderNoResultsManualEdit(
    inputElement: HTMLInputElement,
    suggestionBox: HTMLElement,
    manualEditCallback: () => void,
    hasSuggestionsOrSites: boolean,
  ): void {
    const noResultsElement = this.renderer.createElement('li');
    this.renderer.addClass(noResultsElement, 'address-auto-complete-suggestion-item-not-found');

    if (hasSuggestionsOrSites) {
      this.renderer.addClass(noResultsElement, 'ac-nf-single');
    }

    const textElement = this.renderer.createElement('span');
    this.renderer.appendChild(textElement, this.renderer.createText('Address Not Found'));

    const linkElement = this.renderer.createElement('a');
    this.renderer.appendChild(linkElement, this.renderer.createText('Manual Edit'));
    this.renderer.addClass(linkElement, 'manual-edit-link');
    this.renderer.setAttribute(linkElement, 'href', '#');

    this.renderer.listen(linkElement, 'click', (event: MouseEvent) => {
      event.preventDefault();
      inputElement.value = '';
      if (suggestionBox.parentNode) {
        this.renderer.removeChild(document.body, suggestionBox);
      }
      manualEditCallback();
    });

    this.renderer.appendChild(noResultsElement, textElement);
    this.renderer.appendChild(noResultsElement, linkElement);
    this.renderer.appendChild(suggestionBox, noResultsElement);
  }

  /**
   * Appends a piece of text to an element, wrapping each occurrence of the search term in a
   * highlight span. Everything is added as text nodes, so address data coming back from the
   * lookup providers is never parsed as HTML.
   * @param parent - The element to append the text to.
   * @param text - The text to display.
   * @param searchTerm - The already trimmed and lowercased term to highlight.
   */
  private appendHighlightedText(parent: HTMLElement, text: string, searchTerm: string): void {
    if (!text) return;

    if (!searchTerm) {
      this.renderer.appendChild(parent, this.renderer.createText(text));
      return;
    }

    const normalisedText = text.toLowerCase();
    let position = 0;

    for (
      let matchIndex = normalisedText.indexOf(searchTerm);
      matchIndex !== -1;
      matchIndex = normalisedText.indexOf(searchTerm, position)
    ) {
      if (matchIndex > position) {
        this.renderer.appendChild(
          parent,
          this.renderer.createText(text.substring(position, matchIndex)),
        );
      }

      const highlight = this.renderer.createElement('span');
      this.renderer.addClass(highlight, 'search-term-highlight');
      this.renderer.appendChild(
        highlight,
        this.renderer.createText(text.substring(matchIndex, matchIndex + searchTerm.length)),
      );
      this.renderer.appendChild(parent, highlight);

      position = matchIndex + searchTerm.length;
    }

    if (position < text.length) {
      this.renderer.appendChild(parent, this.renderer.createText(text.substring(position)));
    }
  }

  /**
   * Positions the suggestion box relative to the input element or its parent container.
   */
  private positionSuggestionBox(
    suggestionBox: HTMLElement,
    inputElement: HTMLInputElement,
    parentContainerElement: HTMLElement | null,
  ): void {
    let rect: DOMRect;
    if (parentContainerElement != null) {
      rect = parentContainerElement.getBoundingClientRect();
    } else {
      rect = inputElement.getBoundingClientRect();
    }

    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    this.renderer.setStyle(suggestionBox, 'left', `${rect.left + scrollLeft}px`);
    this.renderer.setStyle(suggestionBox, 'top', `${rect.bottom + scrollTop}px`);
    this.renderer.setStyle(suggestionBox, 'width', `${rect.width}px`);
  }

  /**
   * Attaches a click listener to the document to close the suggestion box when clicking outside it.
   */
  private attachClickOutsideListener(
    suggestionBox: HTMLElement,
    inputElement: HTMLInputElement,
  ): void {
    const handleClickOutsideSuggestionBox = (event: MouseEvent) => {
      if (
        suggestionBox &&
        !suggestionBox.contains(event.target as Node) &&
        event.target !== inputElement
      ) {
        if (suggestionBox.parentNode) {
          this.renderer.removeChild(document.body, suggestionBox);
        }
        document.removeEventListener('click', handleClickOutsideSuggestionBox);
      }
    };
    document.addEventListener('click', handleClickOutsideSuggestionBox);
  }

  public getPrimaryAddress(address: AutoCompleteAddressDetails) {
    const primaryAddressParts = [
      (address.residential ? address.line_1 : address.line_2).trim(),
      (address.residential ? address.line_2 : address.line_3).trim(),
      (address.residential ? address.line_3 : '').trim(),
      address.locality.trim(),
      address.town_or_city.trim(),
      address.postcode.trim(),
    ];

    return primaryAddressParts.filter((part) => part && part.trim().length > 0).join(', ');
  }

  /**
   * Converts address details from getAddress.io to the IVehicleAddress format.
   * @param address - The address details from getAddress.io.
   * @returns The address in IVehicleAddress format.
   */
  public convertToVehicleAddress(address: AutoCompleteAddressDetails): IVehicleAddress {
    const response: IVehicleAddress = {
      id: 0,
      primaryAddress: this.getPrimaryAddress(address),
      secondary: address.line_2 || address.sub_building_name || address.sub_building_number || '',
      city: address.town_or_city,
      postcode: address.postcode,
      country: address.country,
      latitude: address.latitude,
      longitude: address.longitude,
      route: address.thoroughfare,
      postal_town: address.town_or_city,
      street_number: address.building_number,
      addressLine1: address.residential ? address.line_1 : address.line_2,
      addressLine2: address.residential ? address.line_2 : address.line_3,
      addressLine3: address.residential
        ? address.line_3 && address.line_3.trim() != ''
          ? address.line_3
          : address.locality
        : address.locality,
      businessName: address.residential ? '' : address.line_1,
      manualEntry: false,
    };

    response.secondary = response.secondary || '';
    response.route = response.route || '';
    response.postal_town = response.postal_town || '';
    response.street_number = response.street_number || '';
    response.addressLine1 = response.addressLine1 || '';
    response.addressLine2 = response.addressLine2 || '';
    response.addressLine3 = response.addressLine3 || '';
    response.businessName = response.businessName || '';

    return response;
  }

  /**
   * Converts address details from Loqate to the IVehicleAddress format.
   * @param address - The address details from Loqate.
   * @returns The address in IVehicleAddress format.
   */
  public convertLoqateToVehicleAddress(address: AutoCompleteAddressDetailsLoqate): IVehicleAddress {
    const primaryAddressParts = [
      address.Line1.trim(),
      address.Line2.trim(),
      address.Line3.trim(),
      address.City.trim(),
      address.PostalCode.trim(),
    ];

    const primaryAddress = primaryAddressParts
      .filter((part) => part && part.trim().length > 0)
      .join(', ');

    const response: IVehicleAddress = {
      id: 0,
      primaryAddress: primaryAddress,
      secondary: address.Line2 || address.SubBuilding || address.BuildingName || '',
      city: address.City,
      postcode: address.PostalCode,
      country: address.CountryName,
      latitude: parseFloat(address.Field1),
      longitude: parseFloat(address.Field2),
      route: address.Street || address.SecondaryStreet || '',
      postal_town: address.City,
      street_number: address.BuildingNumber,
      addressLine1: address.Line1,
      addressLine2: address.Line2,
      addressLine3: address.Line3,
      businessName: address.Company || '',
      manualEntry: false,
    };

    response.secondary = response.secondary || '';
    response.route = response.route || '';
    response.postal_town = response.postal_town || '';
    response.street_number = response.street_number || '';
    response.addressLine1 = response.addressLine1 || '';
    response.addressLine2 = response.addressLine2 || '';
    response.addressLine3 = response.addressLine3 || '';
    response.businessName = response.businessName || '';

    return response;
  }

  /**
   * Formats a standard getAddress.io address, handling differences between residential and commercial addresses.
   */
  private formatStandardAddress(
    address: AutoCompleteAddressDetails,
  ): AutoCompleteAddressDetailsFormatted {
    const response: AutoCompleteAddressDetailsFormatted = {
      postcode: address.postcode,
      latitude: address.latitude,
      longitude: address.longitude,
      thoroughfare: address.thoroughfare,
      building_name: address.building_name,
      sub_building_name: address.sub_building_name,
      sub_building_number: address.sub_building_number,
      building_number: address.building_number,
      line_1: address.residential ? address.line_1 : address.line_2,
      line_2: address.residential ? address.line_2 : address.line_3,
      line_3: address.residential ? address.line_3 : '',
      line_4: address.line_4,
      locality: address.locality,
      town_or_city: address.town_or_city,
      county: address.county,
      district: address.district,
      country: address.country,
      residential: address.residential,
      businessName: address.residential ? '' : address.line_1,
    };

    return response;
  }

  /**
   * Formats a Loqate address into the standard address format.
   */
  private formatLoqateToStandardAddress(
    address: AutoCompleteAddressDetailsLoqate,
  ): AutoCompleteAddressDetailsFormatted {
    const response: AutoCompleteAddressDetailsFormatted = {
      postcode: address.PostalCode,
      latitude: parseFloat(address.Field1),
      longitude: parseFloat(address.Field2),
      thoroughfare: address.Street || address.SecondaryStreet || '',
      building_name: address.BuildingName || '',
      sub_building_name: address.SubBuilding || '',
      sub_building_number: '',
      building_number: address.BuildingNumber || '',
      line_1: address.Line1 || '',
      line_2: address.Line2 || '',
      line_3: address.Line3 || '',
      line_4: '',
      locality: '',
      town_or_city: address.City || '',
      county: '',
      district: '',
      country: address.CountryName || '',
      residential: !address.Company,
      businessName: address.Company || '',
    };

    return response;
  }

  /**
   * Validates a given address string. It attempts a strict validation, then a lenient one,
   * and can fall back to another service if enabled.
   * @param address - The address string to validate.
   * @param useGoogleFallback - Whether to use the MapService (Google) as a fallback.
   * @returns An observable with the validation result.
   */
  public validateAddress(
    address: string,
    useGoogleFallback: boolean = true,
  ): Observable<AutoCompleteAddressValidationResultHeader> {
    if (!address.trim()) {
      return of(undefined);
    }

    // Temporary change: getAddress.io has shut down. Always use MapService (Google)
    // for address validation regardless of the useGoogleFallback flag.
    // The original getAddress.io strict/lenient flow is intentionally bypassed for now.
    return from(this.mapService.GetAddress(address)).pipe(
      map((result) => this.transformMapServiceAddressResult(result)),
      catchError(() =>
        of({
          status: 404,
          address: null,
        } as AutoCompleteAddressValidationResultHeader),
      ),
    );
  }

  /**
   * Formats an IVehicleAddress object into a displayable string.
   * @param address - The IVehicleAddress to format.
   * @returns A comma-separated address string.
   */
  public formatVehicleAddress(address: IVehicleAddress): string {
    const addressParts = [
      address.addressLine1,
      address.addressLine2,
      address.addressLine3,
      address.city,
      address.postcode,
    ];

    const nonEmptyParts = addressParts.filter((part) => part && part.trim().length > 0);

    return nonEmptyParts.join(', ');
  }

  /**
   * Transforms the result from MapService into the standard validation result format.
   */
  private transformMapServiceAddressResult(
    mapServiceResult: any,
  ): AutoCompleteAddressValidationResultHeader {
    const ukCountries = ['United Kingdom', 'England', 'Scotland', 'Wales', 'Northern Ireland'];
    const isUK = ukCountries.includes(mapServiceResult.country);

    const address: AutoCompleteAddressValidationResult = {
      postcode: mapServiceResult.postal_code.trim(),
      latitude: mapServiceResult.lat,
      longitude: mapServiceResult.lng,
      formatted_address: mapServiceResult.formatted_address.split(', '),
      thoroughfare: mapServiceResult.route,
      building_name: '',
      sub_building_name: '',
      sub_building_number: '',
      building_number: mapServiceResult.street_number,
      line_1: `${mapServiceResult.street_number || ''} ${mapServiceResult.route || ''}`.trim(),
      line_2: '',
      line_3: '',
      line_4: '',
      locality: mapServiceResult.locality,
      town_or_city: mapServiceResult.locality,
      county: mapServiceResult.administrative_area_level_1,
      district: mapServiceResult.administrative_area_level_2,
      country: mapServiceResult.country,
      residential: false,
    };

    return {
      //status: isUK ? 404 : 200,
      status: 200,
      address: address,
    };
  }

  /**
   * Fetches saved customer sites that match a search term.
   */
  private getCustomerSites(searchTerm: string, customerId?: number): Observable<ICustomerSites[]> {
    if (!searchTerm.trim()) {
      return of([]);
    }

    return from(
      this.customerSitesService.getSavedSitesForAutoCompletion(searchTerm.trim(), customerId),
    ).pipe(
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
    );
  }

  /**
   * Fetches address suggestions from Loqate.
   * Handles container items by making subsequent requests to get more detailed address suggestions.
   * @param searchTerm - The term to search for.
   * @returns An observable of Loqate address suggestions.
   */
  public getSuggestionsFromLoqate(
    searchTerm: string,
  ): Observable<AutoCompleteAddressSuggestionLoqate[]> {
    if (!searchTerm.trim()) {
      return of([]);
    }

    const baseUrl = `${this.loqateApiUrl}?key=${this.loqateApiKey}&IsMiddleware=false`;
    const initialUrl = `${baseUrl}&Text=${encodeURIComponent(searchTerm.trim())}`;

    return this.http.get<AutoCompleteAddressResponseLoqate>(initialUrl).pipe(
      switchMap((response) => {
        const addressItems = response.Items.filter((item) => item.Type === 'Address');
        const containerItems = response.Items.filter((item) => item.Type !== 'Address');

        if (containerItems.length > 0) {
          const containerRequests = containerItems.map((containerItem) => {
            const containerUrl = `${baseUrl}&Container=${encodeURIComponent(containerItem.Id)}`;
            return this.http
              .get<AutoCompleteAddressResponseLoqate>(containerUrl)
              .pipe(map((containerResponse) => containerResponse.Items));
          });

          return combineLatest(containerRequests).pipe(
            map((containerResults) => {
              const allItems = containerResults.reduce((acc, val) => acc.concat(val), []);
              return [...addressItems, ...allItems];
            }),
          );
        } else {
          return of(addressItems);
        }
      }),
      catchError((err) => {
        console.log(err);
        return of([]);
      }),
    );
  }

  /**
   * Fetches detailed information for a specific address from Loqate using its ID.
   * @param id - The ID of the Loqate address suggestion.
   * @returns An observable of detailed Loqate address information.
   */
  public getLoqateAddressDetails(id: string): Observable<AutoCompleteAddressDetailsResponseLoqate> {
    const url = `${this.loqateApiUrlForDetails}?key=${this.loqateApiKey}&Id=${encodeURIComponent(
      id,
    )}&Field1Format={Latitude}&Field2Format={Longitude}`;
    return this.http.get<AutoCompleteAddressDetailsResponseLoqate>(url);
  }

  /**
   * Filters out Loqate suggestions that are likely duplicates of suggestions from the main provider.
   * @param mainSuggestions - Suggestions from the main provider (getAddress.io).
   * @param loqateSuggestions - Suggestions from Loqate.
   * @returns A filtered array of Loqate suggestions.
   */
  public filterLoqateSuggestions(
    mainSuggestions: AutoCompleteAddressSuggestion[],
    loqateSuggestions: AutoCompleteAddressSuggestionLoqate[],
  ): AutoCompleteAddressSuggestionLoqate[] {
    const mainAddresses = mainSuggestions.map((suggestion) => ({
      fullAddress: suggestion.address.toLowerCase(),
      houseNumber: this.extractHouseNumber(suggestion.address),
    }));

    const filteredLoqateSuggestions = loqateSuggestions.filter((loqateSuggestion) => {
      const loqateFullAddress =
        `${loqateSuggestion.Text} ${loqateSuggestion.Description}`.toLowerCase();
      const loqateHouseNumber = this.extractHouseNumber(loqateFullAddress);

      const isDuplicate = mainAddresses.some((mainAddress) => {
        const similarity = stringSimilarity.compareTwoStrings(
          loqateFullAddress,
          mainAddress.fullAddress,
        );
        const houseNumberMatch = loqateHouseNumber === mainAddress.houseNumber;

        return similarity > 0.7 && houseNumberMatch;
      });

      return !isDuplicate;
    });

    return filteredLoqateSuggestions;
  }

  /**
   * Extracts the house number from the beginning of an address string.
   */
  private extractHouseNumber(address: string): string {
    const match = address.match(/^\d+/);
    return match ? match[0] : '';
  }

  /**
   * Combines and transforms suggestions from both main and Loqate sources into a single, unified format.
   * @param mainSuggestions - Suggestions from the main provider.
   * @param loqateSuggestions - Suggestions from Loqate.
   * @returns A sorted array of unified address suggestions.
   */
  public transformSuggestions(
    mainSuggestions: AutoCompleteAddressSuggestion[],
    loqateSuggestions: AutoCompleteAddressSuggestionLoqate[],
  ): UnifiedAddressSuggestion[] {
    const transformedMain = mainSuggestions.map((suggestion) => ({
      address: suggestion.address,
      id: suggestion.id,
      source: SuggestionSource.Main,
    }));

    const transformedLoqate = loqateSuggestions.map((suggestion) => ({
      address: `${suggestion.Text} ${suggestion.Description}`,
      id: suggestion.Id,
      source: SuggestionSource.Loqate,
    }));

    return [...transformedMain, ...transformedLoqate].sort((a, b) =>
      a.address.localeCompare(b.address),
    );
  }

  /**
   * A fallback mechanism that uses the MapService to get address details when other providers fail.
   */
  private useMapServiceFallback(
    address: string,
    inputElement: HTMLInputElement,
    suggestionBox: HTMLElement,
    mapServiceCallback: (address: AddressDTO) => void,
  ): void {
    this.mapService
      .GetAddress(address)
      .then((mapResult) => {
        inputElement.value = '';
        mapServiceCallback(mapResult);
        if (suggestionBox.parentNode) {
          this.renderer.removeChild(document.body, suggestionBox);
        }
      })
      .catch((mapError) => {
        console.error('MapService fallback failed', mapError);
      });
  }

  /**
   * Converts an address from the MapService (AddressDTO) to the IVehicleAddress format.
   */
  private convertMapServiceToVehicleAddress(address: AddressDTO): IVehicleAddress {
    const primaryAddressParts = [
      `${address.street_number || ''} ${address.route || ''}`.trim(),
      address.locality || '',
      address.administrative_area_level_1 || '',
      address.postal_code || '',
    ];

    const primaryAddress = primaryAddressParts
      .filter((part) => part && part.trim().length > 0)
      .join(', ');

    return {
      id: 0,
      primaryAddress: primaryAddress,
      secondary: '',
      city: address.locality || '',
      postcode: address.postal_code || '',
      country: address.country || '',
      latitude: address.lat,
      longitude: address.lng,
      route: address.route || '',
      postal_town: address.locality || '',
      street_number: address.street_number || '',
      addressLine1: `${address.street_number || ''} ${address.route || ''}`.trim(),
      addressLine2: '',
      addressLine3: address.locality || '',
      businessName: '',
      manualEntry: false,
    };
  }

  /**
   * Checks if the given latitude and longitude are valid numbers and not (0,0).
   */
  private isLatLngValid(
    lat: number | string | null | undefined,
    lng: number | string | null | undefined,
  ): boolean {
    const latitude = typeof lat === 'string' ? parseFloat(lat) : lat;
    const longitude = typeof lng === 'string' ? parseFloat(lng) : lng;

    if (
      latitude === null ||
      latitude === undefined ||
      isNaN(Number(latitude)) ||
      longitude === null ||
      longitude === undefined ||
      isNaN(Number(longitude))
    ) {
      return false;
    }

    return !(latitude === 0 && longitude === 0);
  }

  /**
   * Creates a formatted address string from getAddress.io details, used for fallbacks.
   */
  private createFormattedAddressStringFromGetAddress(address: AutoCompleteAddressDetails): string {
    const addressParts = [
      address.building_number,
      address.building_name,
      address.thoroughfare,
      address.locality,
      address.town_or_city,
      address.county,
      address.postcode,
      address.country,
    ];

    return addressParts.filter((part) => part && part.trim() !== '').join(', ');
  }

  /**
   * Creates a formatted address string from Loqate details, used for fallbacks.
   */
  private createFormattedAddressStringFromLoqate(
    address: AutoCompleteAddressDetailsLoqate,
  ): string {
    const addressParts = [
      address.BuildingNumber,
      address.BuildingName,
      address.Street,
      address.City,
      address.AdminAreaName,
      address.PostalCode,
      address.CountryName,
    ];

    return addressParts.filter((part) => part && part.trim() !== '').join(', ');
  }

  /**
   * Compares the similarity between an input address string and a found address object.
   */
  private checkAddressSimilarity(
    inputAddress: string,
    foundAddress: AutoCompleteAddressValidationResult,
  ): number {
    const getAddressString = this.formatAddressForComparison(foundAddress);

    const normalisedInput = inputAddress.toLowerCase().trim();

    return stringSimilarity.compareTwoStrings(normalisedInput, getAddressString);
  }

  /**
   * Formats a validation result address into a simple, lowercased string for comparison purposes.
   */
  private formatAddressForComparison(address: AutoCompleteAddressValidationResult): string {
    const addressParts = [
      address.building_number,
      address.building_name,
      address.thoroughfare,
      address.locality,
      address.town_or_city,
      address.county,
      address.postcode,
      address.country,
    ];

    return addressParts
      .filter((part) => part && part.trim() !== '')
      .join(' ')
      .toLowerCase()
      .trim();
  }
}
